import { ValueExtender } from '@ekkogmbh/apisdk';
import { useCallback, useEffect, useState } from 'react';
import RegexInput from 'src/Common/Components/Forms/RegexInput';
import StyledFormAccordion from 'src/Common/Components/Forms/StyledFormAccordion';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import ListStepper from 'src/Common/Components/ListStepper';
import { CenteredColumn } from 'src/Common/Components/Stacks';

const defaultExtender = {
  annotation: '',
  pattern: '/^(.+)$/',
  replacement: '$1_$value',
  description: 'insert parent node value',
};

interface Props {
  label: string;
  extenders: ValueExtender[];
  onChange: (value: ValueExtender[]) => void;
}

export default function ValueExtendersInput({ label, extenders, onChange }: Props): React.JSX.Element {
  const [currentIndex, setCurrentIndex] = useState<number | undefined>(0);
  const [isChanged, setChanged] = useState<boolean>(false);

  const [annotation, setAnnotation] = useState<string>('');
  const [pattern, setPattern] = useState<string>('');
  const [replacement, setReplacement] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  useEffect(() => {
    if (extenders.length === 0) {
      setCurrentIndex(undefined);
      return;
    } else if (currentIndex === undefined) {
      setCurrentIndex(0);
      return;
    }

    if (currentIndex !== undefined) {
      const extender = extenders[currentIndex];
      setAnnotation(extender.annotation);
      setPattern(extender.pattern);
      setReplacement(extender.replacement);
      setDescription(extender.description);
    }
  }, [extenders, currentIndex]);

  useEffect(() => {
    if (!isChanged || currentIndex === undefined) {
      return;
    }

    extenders[currentIndex] = { annotation, pattern, replacement, description };
    onChange([...extenders]);
    setChanged(false);
  }, [currentIndex, extenders, onChange, annotation, pattern, replacement, description]);

  const removeExtender = useCallback(() => {
    if (currentIndex === undefined) {
      return;
    }

    extenders.splice(currentIndex, 1);
    onChange([...extenders]);
  }, [extenders, currentIndex, onChange]);

  const addExtender = useCallback(() => {
    extenders.push(defaultExtender);
    onChange([...extenders]);
  }, [extenders, onChange]);

  return (
    <StyledFormAccordion label={label} centerLabel={extenders.length === 0 ? 'none' : extenders.length.toString()}>
      <CenteredColumn width={'100%'} spacing={0}>
        <ListStepper
          optional
          length={extenders.length}
          index={currentIndex}
          onIndexChange={setCurrentIndex}
          onAdd={addExtender}
          onRemove={removeExtender}
        />
        {currentIndex !== undefined && (
          <CenteredColumn width={'100%'} spacing={1}>
            <StyledTextField
              label={'Annotation'}
              value={annotation}
              onChange={(e) => {
                setAnnotation(e.target.value as string);
                setChanged(true);
              }}
            />
            <StyledTextField
              label={'Description'}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value as string);
                setChanged(true);
              }}
            />
            <RegexInput
              label={'Pattern'}
              value={pattern}
              replacement={replacement}
              onChange={(p) => {
                setPattern(p);
                setChanged(true);
              }}
              onChangeReplacement={(r) => {
                setReplacement(r);
                setChanged(true);
              }}
            />
          </CenteredColumn>
        )}
      </CenteredColumn>
    </StyledFormAccordion>
  );
}
